/**
 * NotificationBell — bell icon en el header con badge count.
 *
 * Auto-detecta nuevos industry jobs (el backend compara contra D1 cada
 * vez que se llama a /api/industry/notifications). Polls cada 60s.
 *
 *  - Bell con dot/badge rojo si unseen_count > 0.
 *  - Click → dropdown panel con lista de unseen + ack buttons.
 *  - "Ack all" → POST /notifications/ack + limpia el badge.
 *  - Click individual → DELETE /notifications/:id + refetch.
 *  - Click fuera del panel → cierra.
 *  - Empty state: "Sin notifications nuevas".
 *
 * Caveats:
 *  - El backend hace on-demand detect, así que cada poll puede traer
 *    notifications nuevas que no estaban en la response anterior.
 *  - Polling se pausa cuando la tab no está visible (TanStack Query
 *    refetchIntervalInBackground=false).
 *  - No usamos web push real todavía — el bell aparece al abrir el
 *    dashboard. Mejora futura: hook con Push API + service worker.
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, formatISKShort } from '../api'

type Notification = {
  job_id: number
  activity_id: number
  activity_name: string
  blueprint_name: string | null
  product_type_id: number | null
  runs: number
  status: string
  start_date: string
  estimated_margin_per_hour: number | null
  materials_note: string | null
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  const { data, isFetching } = useQuery({
    queryKey: ['industry-notifications'],
    queryFn: () => api.industryNotifications(),
    refetchInterval: 60_000,  // poll cada 60s
    refetchIntervalInBackground: false,  // pausa si tab hidden
    staleTime: 30_000,
  })

  const ackAll = useMutation({
    mutationFn: () => api.industryNotificationsAck(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['industry-notifications'] }),
  })

  const ackOne = useMutation({
    mutationFn: (jobId: number) => api.industryNotificationsAckOne(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['industry-notifications'] }),
  })

  // Click outside para cerrar el dropdown
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const notifications: Notification[] = data?.notifications ?? []
  const unseenCount = data?.unseen_count ?? 0

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 rounded text-eve-muted hover:text-eve-text hover:bg-eve-panel/60 transition-colors"
        aria-label={`${unseenCount} notifications sin ver`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {/* Bell SVG — outline 24x24 */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2 L12 4 M6 18 L18 18 L18 11 C18 7.7 15.3 5 12 5 C8.7 5 6 7.7 6 11 L6 18 Z M10 21 L14 21"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unseenCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-eve-danger text-white text-[10px] font-bold font-mono flex items-center justify-center"
            aria-hidden="true"
          >
            {unseenCount > 99 ? '99+' : unseenCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Industry notifications"
          className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] overflow-y-auto bg-eve-panel border border-eve-border rounded-md shadow-xl z-30"
        >
          <div className="sticky top-0 bg-eve-panel border-b border-eve-border px-3 py-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-eve-text">
              Industry notifications
            </span>
            {unseenCount > 0 && (
              <button
                onClick={() => ackAll.mutate()}
                disabled={ackAll.isPending}
                className="text-xs text-eve-accent hover:text-eve-text transition-colors disabled:opacity-50"
              >
                {ackAll.isPending ? 'Acking…' : 'Ack all'}
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="px-4 py-6 text-center text-eve-muted text-sm italic">
              Sin notifications nuevas.
            </div>
          ) : (
            <ul className="divide-y divide-eve-border">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.job_id}
                  n={n}
                  onAck={() => ackOne.mutate(n.job_id)}
                  acking={ackOne.isPending && ackOne.variables === n.job_id}
                />
              ))}
            </ul>
          )}

          {isFetching && (
            <div className="px-3 py-1.5 text-[10px] text-eve-muted text-center italic border-t border-eve-border">
              refreshing…
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function NotificationRow({
  n,
  onAck,
  acking,
}: {
  n: Notification
  onAck: () => void
  acking: boolean
}) {
  const margin = n.estimated_margin_per_hour
  const marginPositive = margin !== null && margin > 0

  return (
    <li className="px-3 py-2.5 hover:bg-eve-bg/40 transition-colors">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Link
              to="/industry"
              className="font-medium text-sm truncate text-eve-text hover:text-eve-accent transition-colors"
              title={`Job #${n.job_id}`}
            >
              {n.blueprint_name ?? `BP #${n.job_id}`}
            </Link>
            {n.runs > 1 && (
              <span className="text-xs text-eve-muted font-mono">× {n.runs}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-eve-muted flex-wrap">
            <span>{n.activity_name}</span>
            <span>·</span>
            <span className="font-mono">{formatRelative(n.start_date)}</span>
          </div>
          {margin !== null && (
            <div
              className={`text-xs font-mono mt-1 ${
                marginPositive ? 'text-eve-accent' : 'text-eve-muted'
              }`}
              title="Margin/h estimado basado en el estado actual del mercado"
            >
              {marginPositive ? '+' : ''}
              {formatISKShort(margin)} ISK/h
            </div>
          )}
          {n.materials_note && (
            <div className="text-[10px] text-eve-warn italic mt-0.5">
              {n.materials_note}
            </div>
          )}
        </div>
        <button
          onClick={onAck}
          disabled={acking}
          className="shrink-0 text-[10px] uppercase tracking-wider text-eve-muted hover:text-eve-text transition-colors disabled:opacity-50 px-1.5 py-0.5"
          aria-label={`Ack job ${n.job_id}`}
        >
          {acking ? '…' : '✕'}
        </button>
      </div>
    </li>
  )
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const min = Math.floor(diffMs / 60_000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `hace ${hr}h`
  const days = Math.floor(hr / 24)
  return `hace ${days}d`
}
